import { DeskGroupModel } from '../api/core/core.types';
import {
  PricePeriodType,
  RatePlanListAllModel, RatePlanPeriodModel, RateType
} from '../api/pricing/pricing.types';
import dayjs from 'dayjs';

function filterRatePlans(ratePlans: RatePlanListAllModel[], deskGroupId: number, coreProductId: number, ratePlanId: number) {
  const filteredPlans: RatePlanListAllModel[] = [];

  ratePlans.forEach((rp) => {
    if (rp.rateType === RateType.BASE_RATE && rp.pickupDeskGroupId === deskGroupId && rp.coreProductId === coreProductId && ratePlanId !== rp.id) {
      filteredPlans.push(rp);
    }
  });

  return filteredPlans;
}

export function sortRatePlansByLocationGroup(ratePlans: RatePlanListAllModel[], deskGroups: DeskGroupModel[]) {
  const locationGroupRatePlanMap = new Map();
  ratePlans.forEach((ratePlan) => {
    const deskGroup = deskGroups.find((dg) => dg.id === ratePlan.pickupDeskGroupId);

    if (deskGroup) {
      if (!(locationGroupRatePlanMap.has(deskGroup.name))) locationGroupRatePlanMap.set(deskGroup.name, [ ratePlan ]);
      else locationGroupRatePlanMap.get(deskGroup.name).push(ratePlan);
    }
  });

  return locationGroupRatePlanMap;
}

const adjustmentsOverlap = (subject: RatePlanListAllModel, target: RatePlanListAllModel) => {
  if ((dayjs(subject.validTo) < dayjs(target.validFrom) || (dayjs(subject.validFrom) > dayjs(target.validTo)))) return false;

  return true;
};

const adjustmentFitsInRow = (subjectAdjustment: RatePlanListAllModel, adjustments: RatePlanListAllModel[]) => {
  if (!adjustments.length) return true;
  // eslint-disable-next-line no-restricted-syntax
  for (const i of adjustments) {
    if (adjustmentsOverlap(subjectAdjustment, i)) return false;
  }

  return true;
};

export function getRatePlanTimelineMatrix(ratePlans: RatePlanListAllModel[]) {
  const matrix: RatePlanListAllModel[][] = [ [] ];
  if (ratePlans && ratePlans.length) {
    let adjustments: RatePlanListAllModel[] = [];
    let standardRates: RatePlanListAllModel[] = [];

    ratePlans.forEach((ratePlan) => {
      if (ratePlan.rateType === RateType.ADJUSTMENT) adjustments.push(ratePlan);
      else standardRates.push(ratePlan);
    });

    adjustments = adjustments.sort((a, b) => a.priority - b.priority);
    standardRates = standardRates.sort((a, b) => dayjs(a.validFrom).diff(b.validFrom));
    matrix.push(standardRates);

    let mainAdjustmentRowIndex = 0;
    adjustments.forEach((adjustment) => {
      let isAdjustmentFitted = false;

      if (adjustmentFitsInRow(adjustment, matrix[mainAdjustmentRowIndex])) {
        matrix[mainAdjustmentRowIndex].push(adjustment);
        isAdjustmentFitted = true;
      } else {
        if (mainAdjustmentRowIndex === 0) {
          matrix.unshift([ adjustment ]);
          isAdjustmentFitted = true;
          mainAdjustmentRowIndex++;
        }

        let targetRowIndex = mainAdjustmentRowIndex - 1;
        while (targetRowIndex >= 0 && !isAdjustmentFitted) {
          if (adjustmentFitsInRow(adjustment, matrix[targetRowIndex])) {
            matrix[targetRowIndex].push(adjustment);
            isAdjustmentFitted = true;
          }

          targetRowIndex--;
        }

        if (!isAdjustmentFitted) {
          matrix.unshift([ adjustment ]);
          mainAdjustmentRowIndex++;
          isAdjustmentFitted = true;
        }
      }
    });

    matrix.forEach((line, index) => {
      if (index !== matrix.length - 1) {
        matrix[index] = line.sort((a, b) => dayjs(a.validFrom).diff(b.validFrom));
      }
    });

  }
  return matrix;
}

export function getSurroundingRatePlans(
  ratePlans: RatePlanListAllModel[],
  validFrom: string,
  planId: number,
  deskGroup: number,
  coreProductId: number
) {
  const filteredPlans = filterRatePlans(ratePlans, deskGroup, coreProductId, planId);

  const validFromCorrected = dayjs(validFrom).format('YYYY-MM-DD');
  const followingPlan = filteredPlans.find((rp) => dayjs(rp.validFrom) >= dayjs(validFromCorrected));

  if (followingPlan) {
    const indexOfFollowingPlan = filteredPlans.indexOf(followingPlan);

    if (indexOfFollowingPlan === 0) {
      return { precedingPlan: undefined, followingPlan };
    }

    return { precedingPlan: filteredPlans[indexOfFollowingPlan - 1], followingPlan };
  }
  if (!filteredPlans.length) return { precedingPlan: undefined, followingPlan: undefined };

  return { precedingPlan: filteredPlans[filteredPlans.length - 1], followingPlan: undefined };
}

export function getRateTableColumns(ratePlanPeriods: RatePlanPeriodModel[], isAdjustment?: boolean) {
  const tableRatesAreaWidthPercentage = 65;
  const sortedRatePlanPeriods = ratePlanPeriods.sort((a: RatePlanPeriodModel, b: RatePlanPeriodModel) => a.daysFrom - b.daysFrom);

  let columns;
  if (isAdjustment) columns = [ { label: 'l.rate_plan.acriss_code', width: '7%' },
    { label: 'l.rate_plan.model', width: '25%' } ];
  else columns = [ { label: 'l.rate_plan.acriss_code', width: '10%' }, { label: 'l.rate_plan.model', width: '25%' } ];

  const rateColumnWith = tableRatesAreaWidthPercentage / sortedRatePlanPeriods.length;

  sortedRatePlanPeriods.forEach((rpp, index) => {
    let daysTo = '+';
    if (index + 1 < ratePlanPeriods.length) {
      if (rpp.daysFrom === ratePlanPeriods[index + 1].daysFrom - 1) daysTo = String(rpp.daysFrom);
      else daysTo = `-${ratePlanPeriods[index + 1].daysFrom - 1}`;
    }

    columns.push({ label: `${rpp.daysFrom}${String(rpp.daysFrom) === daysTo ? '' : daysTo} (${rpp.pricePeriodType === PricePeriodType.FIXED ? 'fixed' : 'per day'})`, width: `${rateColumnWith}%` });
  });

  return columns;
}
